<template>
  <div
    class="preview-status"
    @click="handleClick"
  >
    <div
      :class="['preview-status__image-container', { 'preview-status__image-container--border': showBorder }]"
      :style="{ borderColor: color }"
    >
      <image-viewer
        :src="imageSrc"
        :max-width="maxImageWidth"
        :max-height="maxImageHeight"
        class="preview-status__image-container__image"
      >
        <loading-spinner
          :size="30"
          :is-loading="loading"
          class="preview-status__image-container__loader"
        />
        <v-overlay
          v-if="loading"
          absolute
        />
      </image-viewer>
      <span
        v-if="imageLabel"
        :class="[
          'preview-status__image-container__label',
          { 'preview-status__image-container__label--overlay': loading },
        ]"
        :style="{
          backgroundColor: color,
        }"
      >
        {{ imageLabel }}
      </span>
    </div>
    <template v-if="$slots.footer">
      <slot name="footer" />
    </template>
    <div
      v-else
      class="mt-2 d-flex flex-column"
    >
      <span class="preview-status__label">{{ label }}</span>
      <span class="preview-status__subtitle">{{ subtitle }}</span>
    </div>
    <v-overlay
      v-if="disabled"
      absolute
      color="white"
      opacity="0.5"
    />
  </div>
</template>

<script lang="ts" setup>
import ImageViewer from '../ImageViewer/ImageViewer.vue';
import LoadingSpinner from '../loaders/LoadingSpinner.vue';

interface Props {
  imageSrc: string;
  color?: string;
  disabled?: boolean;
  imageLabel?: string;
  label?: string;
  loading?: boolean;
  maxImageHeight?: string | number;
  maxImageWidth?: string | number;
  showBorder?: boolean;
  subtitle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  color: '',
  disabled: false,
  imageLabel: '',
  label: '',
  loading: false,
  maxImageHeight: 'none',
  maxImageWidth: 'none',
  showBorder: false,
  subtitle: '',
});

const emit = defineEmits<{
  (e: 'click'): void;
}>();

const handleClick = () => {
  if (!props.loading && !props.disabled) emit('click');
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/main.scss';

.preview-status {
  position: relative;

  &__image-container {
    border-radius: 5px;
    position: relative;
    box-shadow: 0px 0px 0px 5px rgb(255, 255, 255) inset;

    &--border {
      border: 3px solid;
      padding: 2px;
    }

    &__image {
      border-radius: 5px;
    }

    &__loader {
      z-index: 10;
    }

    &__label {
      @include subtitle-2;
      color: #fff;
      padding: 2px 12px;
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
      position: absolute;
      bottom: 0px;
      left: 50%;
      transform: translateX(-50%);
      white-space: nowrap;

      &--overlay {
        opacity: 0.5;
      }
    }
  }

  &__label {
    @include subtitle-2;
    color: $primary;
    display: inline-block;
    white-space: nowrap;
  }

  &__subtitle {
    @include body-3;
    color: $secondaryMedium;
    display: block;
  }
}
</style>
